import React from 'react';
import { Check, ClipboardList, Heart, Pencil, RotateCcw, Trash2, Loader2, X, History } from 'lucide-react';
import { formatProductLabel, formatIngredientDisplay } from '../utils/formatDisplayLabel';

const statusClass = (s = '') => {
  const t = s.toLowerCase();
  if (t === 'cancelled') return 'bg-slate-100 text-slate-500';
  if (t.includes('not')) return 'bg-red-100 text-red-700';
  if (t.includes('maybe') || t.includes('unknown')) return 'bg-amber-100 text-amber-800';
  if (t.includes('vegan') || t.includes('plant')) return 'bg-emerald-100 text-emerald-700';
  return 'bg-slate-100 text-slate-700';
};

const statusBorderClass = (s = '') => {
  const t = s.toLowerCase();
  if (t === 'cancelled') return 'border-l-slate-300';
  if (t.includes('not')) return 'border-l-red-500';
  if (t.includes('maybe') || t.includes('unknown')) return 'border-l-amber-400';
  if (t.includes('vegan') || t.includes('plant')) return 'border-l-emerald-500';
  return 'border-l-slate-300';
};

const statusBgClass = (s = '') => {
  const t = s.toLowerCase();
  if (t === 'cancelled') return 'bg-slate-50/90';
  if (t.includes('not')) return 'bg-red-50/80';
  if (t.includes('maybe') || t.includes('unknown')) return 'bg-amber-50/80';
  if (t.includes('vegan') || t.includes('plant')) return 'bg-white/90';
  return 'bg-white/90';
};

const formatStatusLabel = (s = '') => {
  const t = s.toLowerCase();
  if (t === 'cancelled') return 'Cancelled';
  if (t.includes('not')) return 'Not Vegan';
  if (t.includes('maybe') || t.includes('unknown')) return 'Maybe';
  if (t.includes('vegan') || t.includes('plant')) return 'Vegan';
  return 'Unknown';
};

const fmtConfidence = (c) => c == null ? null : Math.round((c > 1 ? c : c * 100));

const historyActionButtonClass = 'flex min-w-0 items-center justify-center gap-2 rounded-2xl border bg-white/75 px-3 py-2 text-[11px] font-bold leading-none transition-colors';

export function HistorySidebar({
  items,
  onRerun,
  onRemove,
  onFavorite = () => {},
  isFavorite = () => false,
  onUpdateTitle = () => {},
  onClear,
  rerunId,
  hidden = false,
  onToggleHidden = () => {},
  topOffsetClassName = 'md:top-[76px]'
}) {
  const [filter, setFilter] = React.useState('all');
  const [expandedId, setExpandedId] = React.useState(null);
  const [editingId, setEditingId] = React.useState(null);
  const [draftTitle, setDraftTitle] = React.useState('');

  const filtered = items.filter(i => {
    if (filter === 'all') return true;
    if (filter === 'cancelled') return i.cancelled === true;
    return i.mode === filter;
  });

  // Lock background scroll while the mobile drawer is open so the page
  // underneath doesn't scroll when users swipe inside the drawer.
  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    if (hidden) return;
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    if (!isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [hidden]);

  const filterLabels = {
    all: 'All',
    manual: 'Manual',
    product: 'Product',
    scanner: 'Scanner',
    cancelled: 'Cancelled',
  };

  // Same list/footer markup is shared between the desktop sidebar and the
  // mobile drawer to keep the two surfaces in lockstep.
  const renderBody = ({ withMobileHeader }) => (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Header (shared by desktop and mobile) */}
      <div className="flex items-center justify-between px-5 pt-5 pb-3">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/85 text-emerald-600 shadow-sm shadow-emerald-950/5">
            <History className="h-4 w-4" />
          </span>
          <span className="text-2xl font-black tracking-tight text-slate-900">History</span>
          {items.length > 0 && (
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700">
              {items.length}
            </span>
          )}
        </div>
        {withMobileHeader && (
          <button
            type="button"
            onClick={() => onToggleHidden(true)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-white/80 text-slate-500 shadow-sm shadow-emerald-950/5 transition-colors hover:bg-emerald-100 hover:text-emerald-700 md:hidden"
            aria-label="Close history"
            title="Close history"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="px-4 pb-3">
        <div className="flex flex-wrap gap-1.5">
          {['all','manual','product','scanner','cancelled'].map(k => (
            <button
              key={k}
              type="button"
              onClick={() => setFilter(k)}
              className={`rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors ${filter===k ? 'border-emerald-600 bg-emerald-600 text-white shadow-sm shadow-emerald-700/20' : 'border-emerald-100/80 bg-white/80 text-slate-600 hover:border-emerald-300 hover:text-emerald-700'}`}
            >
              {filterLabels[k]}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-4 pb-4 pt-2 space-y-4">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-2 pt-12 pb-6 text-center">
            <div className="relative mb-4 inline-flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50">
              <ClipboardList className="h-9 w-9 text-emerald-600/75" aria-hidden="true" />
            </div>
            <div className="text-base font-black text-slate-900">No history yet</div>
            <div className="mt-1 text-sm text-slate-500">Products you check<br/>will appear here.</div>
          </div>
        ) : filtered.map(item => {
          const c = fmtConfidence(item.confidence); // eslint-disable-line no-unused-vars
          const isExpanded = expandedId === item.id;
          const saved = isFavorite(item);
          const hasExpandableContent =
            (item.productMatch && item.input) ||
            item.nonPlantBasedIngredients?.length > 0 ||
            item.questionableIngredients?.length > 0;

          return (
            <div key={item.id} className={`rounded-[1.5rem] border border-white/80 border-l-4 p-4 shadow-lg shadow-emerald-950/5 transition-transform hover:-translate-y-0.5 ${statusBorderClass(item.status)} ${statusBgClass(item.status)}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-emerald-600">
                  {item.mode}
                </span>
                <span className="text-[10px] text-slate-400 tabular-nums whitespace-nowrap">
                  {new Date(item.ts).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}
                </span>
              </div>

              <div className="mt-2 text-left w-full">
                <div className="mt-2 text-slate-900">
                  {editingId === item.id ? (
                    <form
                      className="flex gap-1.5"
                      onSubmit={(event) => {
                        event.preventDefault();
                        onUpdateTitle(item.id, draftTitle);
                        setEditingId(null);
                      }}
                    >
                      <input
                        value={draftTitle}
                        onChange={(event) => setDraftTitle(event.target.value)}
                        className="min-w-0 flex-1 rounded-xl border border-emerald-200 bg-white px-3 py-2 text-sm font-bold text-slate-900 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                        aria-label="History entry title"
                        maxLength={80}
                        autoFocus
                      />
                      <button
                        type="submit"
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-emerald-200 bg-emerald-600 text-white transition-colors hover:bg-emerald-700"
                        aria-label="Save title"
                        title="Save title"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                    </form>
                  ) : (
                    <button
                      type="button"
                      onClick={() => hasExpandableContent && setExpandedId(isExpanded ? null : item.id)}
                      className={`w-full text-left ${hasExpandableContent ? '' : 'cursor-default'}`}
                    >
                      <div className={`text-base font-black leading-snug ${isExpanded ? '' : 'line-clamp-2'}`}>
                        {formatProductLabel(item.historyLabel || item.productMatch || item.input || '')}
                      </div>
                    </button>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${statusClass(item.status)}`}>
                      {formatStatusLabel(item.status)}
                    </span>
                    {item.mode === 'scanner' && (
                      <span className="text-[11px] font-medium text-slate-500">Ingredient photo</span>
                    )}
                  </div>
                  {item.allergens?.length > 0 && (
                    <div className="text-[10px] text-orange-600 mt-2 flex items-center gap-0.5">
                      ⚠️ <span className="font-medium">Allergens:</span>{' '}
                      {item.allergens.map((a) => formatProductLabel(String(a))).join(', ')}
                    </div>
                  )}
                </div>
                {isExpanded && (
                  <div className="mt-3 space-y-1 border-t border-emerald-100 pt-3 text-[11px] text-slate-600">
                    {item.historyLabel && (
                      <div><span className="text-slate-400 font-bold uppercase tracking-wide text-[9px]">Scan</span> {item.input}</div>
                    )}
                    {item.productMatch && item.input && (
                      <div className="break-words"><span className="text-slate-400 font-bold uppercase tracking-wide text-[9px]">Input</span> {item.input}</div>
                    )}
                    {item.nonPlantBasedIngredients?.length > 0 && (
                      <div>
                        <span className="text-slate-400 font-bold uppercase tracking-wide text-[9px]">Not vegan</span>{' '}
                        {item.nonPlantBasedIngredients.map((x) => formatIngredientDisplay(String(x))).join(', ')}
                      </div>
                    )}
                    {item.questionableIngredients?.length > 0 && (
                      <div>
                        <span className="text-slate-400 font-bold uppercase tracking-wide text-[9px]">Questionable</span>{' '}
                        {item.questionableIngredients.map((x) => formatIngredientDisplay(String(x))).join(', ')}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => onFavorite(item)}
                  aria-pressed={saved}
                  className={`${historyActionButtonClass} ${
                    saved
                      ? 'border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300'
                      : 'border-rose-200 text-rose-600 hover:bg-rose-50 hover:border-rose-300'
                  }`}
                  title={saved ? 'Remove from favorites' : 'Save to favorites'}
                >
                  <Heart className={`h-4 w-4 shrink-0 ${saved ? 'fill-emerald-600 text-emerald-600' : ''}`} />
                  <span className="whitespace-nowrap">{saved ? 'Saved' : 'Save'}</span>
                </button>
                {item.mode === 'scanner' && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(item.id);
                      setDraftTitle(item.historyLabel || '');
                    }}
                    className={`${historyActionButtonClass} border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300`}
                  >
                    <Pencil className="h-4 w-4 shrink-0" />
                    <span className="whitespace-nowrap">Edit</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onRerun(item)}
                  disabled={rerunId === item.id}
                  className={`${historyActionButtonClass} border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-300 disabled:opacity-50`}
                >
                  {rerunId === item.id ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <RotateCcw className="h-4 w-4 shrink-0" />}
                  <span className="whitespace-nowrap">Rescan</span>
                </button>
                <button
                  type="button"
                  onClick={() => onRemove(item.id)}
                  className={`${historyActionButtonClass} border-orange-200 text-orange-600 hover:bg-orange-50 hover:border-orange-300`}
                >
                  <Trash2 className="h-4 w-4 shrink-0" />
                  <span className="whitespace-nowrap">Delete</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="border-t border-emerald-100/80 px-4 py-3">
        <button
          type="button"
          onClick={onClear}
          className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-red-200/70 bg-white/70 py-2 text-sm font-semibold text-red-500 transition-colors hover:bg-red-50 hover:text-red-600 hover:border-red-300"
        >
          <Trash2 className="h-4 w-4" />
          <span>Clear history</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Fixed left sidebar (desktop) */}
      <aside
        className={`fixed bottom-0 left-0 z-30 hidden h-auto w-64 transform flex-col border-r border-emerald-100/60 bg-gradient-to-b from-emerald-50/70 via-white/80 to-amber-50/30 shadow-sm transition-transform duration-200 md:flex ${topOffsetClassName} ${hidden ? '-translate-x-full' : 'translate-x-0'}`}
      >
        {renderBody({ withMobileHeader: false })}
      </aside>

      {/* Mobile drawer + backdrop. Renders unconditionally so the slide
          transition animates in/out, but pointer-events are disabled when
          hidden so it doesn't intercept taps on the page underneath. */}
      <div
        className={`fixed inset-0 z-[60] md:hidden transition-opacity duration-200 ${hidden ? 'pointer-events-none opacity-0' : 'opacity-100'}`}
        aria-hidden={hidden}
      >
        <button
          type="button"
          onClick={() => onToggleHidden(true)}
          className="absolute inset-0 h-full w-full cursor-default bg-black/40"
          aria-label="Close history"
          tabIndex={hidden ? -1 : 0}
        />
        <aside
          className={`absolute inset-y-0 left-0 flex w-[88vw] max-w-[380px] flex-col border-r border-emerald-100 bg-gradient-to-b from-emerald-50 via-white to-amber-50/40 shadow-xl transition-transform duration-200 ${hidden ? '-translate-x-full' : 'translate-x-0'}`}
          role="dialog"
          aria-label="Scan history"
        >
          {renderBody({ withMobileHeader: true })}
        </aside>
      </div>
    </>
  );
}
